import React, { useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { Clock, AlertTriangle, X } from 'lucide-react';
import { isIOSApp } from '../utils/platform';

const TrialCountdownBanner = () => {
  const { user: clerkUser } = useUser();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  // Get trial_end from Clerk public metadata (Unix timestamp)
  const trialEnd = clerkUser?.publicMetadata?.trial_end;
  const subscriptionStatus = clerkUser?.publicMetadata?.subscription_status;
  const plan = clerkUser?.publicMetadata?.plan;

  // Don't show if no trial_end set or user is on free plan without trial
  if (!trialEnd) return null;

  // If user has active Stripe subscription, they've converted to paid — hide trial banner
  if (subscriptionStatus === 'active') return null;

  // Calculate days remaining
  const now = new Date();
  const trialEndDate = new Date(trialEnd * 1000);
  const diffMs = trialEndDate - now;
  const daysRemaining = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));

  // If trial_end is in the past AND no active subscription, trial expired — don't show banner
  // (They'll be downgraded to FREE by the backend)
  if (diffMs < 0) return null;

  // If user is on free_user plan (already downgraded), don't show
  if (plan === 'free_user') return null;

  const isLastDay = daysRemaining <= 1;
  const isUrgent = daysRemaining <= 2;

  // Last day banner cannot be dismissed
  if (dismissed && !isLastDay) return null;

  const handleUpgrade = async () => {
    // Navigate to /subscribe where user can choose monthly/annual
    // On iOS: open in external browser to comply with App Store guidelines
    if (isIOSApp()) {
      if (window.Capacitor?.Plugins?.Browser) {
        await window.Capacitor.Plugins.Browser.open({ url: 'https://ineednumbers.com/subscribe' });
      } else {
        window.open('https://ineednumbers.com/subscribe', '_blank');
      }
    } else {
      navigate('/subscribe');
    }
  };

  const handleDownloadData = () => {
    window.location.href = '/account';
  };

  // LAST DAY — Red warning banner
  if (isLastDay) {
    return (
      <div className="bg-red-600 text-white px-4 py-3 shadow-md" style={{ zIndex: 50 }}>
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 flex-shrink-0 animate-pulse" />
            <span className="text-sm sm:text-base font-semibold">
              Your free trial ends today! Subscribe now to keep access, or download your data.
            </span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={handleUpgrade}
              
              className="px-4 py-1.5 bg-white text-red-600 font-semibold rounded-lg text-sm hover:bg-red-50 transition-colors disabled:opacity-50"
            >
              'Subscribe Now'
            </button>
            <button
              onClick={handleDownloadData}
              className="px-4 py-1.5 bg-red-700 text-white font-medium rounded-lg text-sm hover:bg-red-800 transition-colors border border-red-400"
            >
              Download Data
            </button>
          </div>
        </div>
      </div>
    );
  }

  // URGENT (2 days left) — Purple, not dismissible
  if (isUrgent) {
    return (
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2.5 shadow-sm" style={{ zIndex: 50 }}>
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2">
          <button
            onClick={handleUpgrade}
            
            aria-label="Subscribe to PRO"
            className="bg-transparent border-0 p-0 m-0 text-left flex items-center gap-2 hover:opacity-90 disabled:opacity-50"
          >
            <Clock className="w-4 h-4 flex-shrink-0" />
            <span className="text-sm font-medium">
              <strong>{daysRemaining} day{daysRemaining !== 1 ? 's' : ''}</strong> left in your free trial —{' '}
              <span className="underline font-semibold">
                {subscribing ? 'loading...' : 'subscribe now'}
              </span>{' '}
              to keep PRO access
            </span>
          </button>
        </div>
      </div>
    );
  }

  // NORMAL (3-7 days) — Purple, dismissible
  return (
    <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2.5 shadow-sm" style={{ zIndex: 50 }}>
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <button
          onClick={handleUpgrade}
          
          aria-label="Subscribe to PRO"
          className="bg-transparent border-0 p-0 m-0 text-left flex items-center gap-2 hover:opacity-90 disabled:opacity-50"
        >
          <Clock className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm font-medium">
            <strong>{daysRemaining} days</strong> left in your free PRO trial
          </span>
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={handleUpgrade}
            
            className="inline-flex px-3 py-1 bg-white text-purple-600 font-semibold rounded text-xs sm:text-sm hover:bg-purple-50 transition-colors disabled:opacity-50 whitespace-nowrap"
          >
            Subscribe
          </button>
          <button
            onClick={() => setDismissed(true)}
            className="text-white/70 hover:text-white p-1"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default TrialCountdownBanner;
